import { useEffect, useState, type FormEvent } from 'react';
import { LuSmartphone, LuRefreshCw, LuTriangleAlert, LuCircleCheck } from 'react-icons/lu';
import CxPainel from './CxPainel';
import { useConectarQr } from '../../hooks/useConectarQr';

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * Conexão já existente para reconectar (número desconectado, instância
     * recriada). Sem ela, o painel pede o nome e cria uma conexão nova.
     */
    conexaoId?: number | null;
    nome?: string;
    /** O número leu o código e pareou: a lista recarrega. */
    onConectado: () => void;
}

/**
 * Conectar WhatsApp via QR Code — desenho 2.6 da proposta. A API cria a
 * conexão como Inativa e a promove a Ativa quando o número pareia.
 *
 * O ciclo (criar, consultar o código, apagar se desistir) mora em
 * `useConectarQr` desde 28/10/2026, porque o guia de boas-vindas mostra o
 * mesmo código dentro do próprio bloco; aqui fica só o painel.
 */
const ConectarQrPanel = ({ open, onClose, conexaoId = null, nome, onConectado }: Props) => {
    const { fase, qr, msg, idAtual, criar, recriar, tentarDeNovo, ocupado } = useConectarQr({
        ativo: open,
        conexaoId,
        onConectado,
    });
    const [nomeConexao, setNomeConexao] = useState('');
    const [erroNome, setErroNome] = useState('');

    /* O nome é do painel, não do ciclo: quando o ciclo saiu para `useConectarQr`
       (28/10/2026), a limpeza do campo veio junto — sem isto, reabrir o painel
       traz o nome e o erro da abertura anterior, e o nome vira o da instância
       no servidor, que não muda depois. */
    useEffect(() => {
        if (open) { setNomeConexao(''); setErroNome(''); }
    }, [open]);

    /* O nome vira também o nome da instância na Evolution Go, que não muda
       depois: por isso é pedido antes de criar, e não editado só na lista. */
    const enviarNome = async (e?: FormEvent) => {
        e?.preventDefault();
        if (fase !== 'nome') return;
        const n = nomeConexao.trim();
        if (!n) { setErroNome('Informe o nome da conexão.'); return; }
        setErroNome('');
        await criar(n);
    };

    /* `parado` é o instante entre abrir o painel e o ciclo começar: para uma conexão
       nova ele já mostra o formulário do nome, senão o primeiro quadro pisca o
       retângulo de "gerando o código" antes do campo aparecer. */
    const pedindoNome = fase === 'nome' || fase === 'criando' || (fase === 'parado' && conexaoId === null);

    return (
        <CxPainel
            open={open}
            onClose={onClose}
            icone={<span className="cx-bx is-qr is-lg" aria-hidden="true"><LuSmartphone size={18} /></span>}
            title={conexaoId !== null ? `Reconectar ${nome ?? 'WhatsApp'}` : 'Conectar WhatsApp via QR Code'}
            subtitle="Leia o código no celular, como no WhatsApp Web"
            footer={
                <>
                    <button type="button" className="rp-btn" onClick={onClose}>{fase === 'conectado' ? 'Fechar' : 'Cancelar'}</button>
                    {pedindoNome ? (
                        <button type="submit" form="cx-qr-nome" className="rp-btn is-primary" disabled={fase === 'criando'}>
                            {fase === 'criando' ? 'Criando…' : 'Gerar código'}
                        </button>
                    ) : fase === 'recriar' ? (
                        <button type="button" className="rp-btn is-primary" onClick={recriar}>
                            <LuRefreshCw size={13} /> Recriar instância
                        </button>
                    ) : fase !== 'conectado' && (
                        <button type="button" className="rp-btn" onClick={tentarDeNovo} disabled={ocupado || idAtual === null}>
                            <LuRefreshCw size={13} /> {ocupado ? 'Gerando…' : 'Gerar novo código'}
                        </button>
                    )}
                </>
            }
        >
            {pedindoNome ? (
                <form id="cx-qr-nome" className="cx-qr-form" onSubmit={enviarNome} noValidate>
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="cx-qr-nome-input">Nome da conexão</label>
                        <input id="cx-qr-nome-input" className={`rp-input${erroNome ? ' has-error' : ''}`} type="text"
                            maxLength={255} autoFocus placeholder="Ex.: Comercial João"
                            value={nomeConexao} disabled={fase === 'criando'}
                            onChange={e => { setNomeConexao(e.target.value); setErroNome(''); }} />
                        {erroNome
                            ? <span className="rp-error">{erroNome}</span>
                            : <span className="rp-hint">Também vira o nome da instância no servidor de conexão e não muda depois. Renomear na lista muda só o nome exibido no Brik.</span>}
                    </div>
                    {msg && (
                        <div className="cx-qr-ok is-erro">
                            <LuTriangleAlert size={20} />
                            <span>{msg}</span>
                        </div>
                    )}
                </form>
            ) : (
                <div className="cx-qr">
                    {fase === 'conectado' ? (
                        <div className="cx-qr-ok">
                            <LuCircleCheck size={28} />
                            <b>Número conectado.</b>
                            <span>Ele já aparece na lista como WhatsApp (via QR Code).</span>
                        </div>
                    ) : fase === 'erro' || fase === 'recriar' || fase === 'expirado' ? (
                        <div className="cx-qr-ok is-erro">
                            <LuTriangleAlert size={24} />
                            <span>{msg}</span>
                        </div>
                    ) : qr ? (
                        <img src={qr} alt="QR Code para conectar o WhatsApp" width={220} height={220} />
                    ) : (
                        <div className="cx-qr-skel" aria-label="Gerando o código…" />
                    )}
                    {fase === 'lendo' && <p className="cx-qr-t"><b>Aguardando leitura…</b> o código é renovado sozinho até o número parear.</p>}
                    {fase === 'gerando' && <p className="cx-qr-t">Iniciando a sessão. O código aparece em alguns segundos.</p>}
                </div>
            )}

            <ol className="cx-qr-passos">
                <li><span className="cx-qr-n">1</span><span>No celular, abra o WhatsApp › Dispositivos conectados › Conectar dispositivo.</span></li>
                <li><span className="cx-qr-n">2</span><span>Aponte a câmera para o código acima.</span></li>
                <li><span className="cx-qr-n">3</span><span>Quando conectar, a linha fica <b>Ativa</b> na lista como <b>WhatsApp (via QR Code)</b>.</span></li>
            </ol>

            <div className="cx-nota">
                <LuTriangleAlert size={14} />
                <span>Este número depende do celular ligado e com internet. Se ele desligar, as mensagens param de entrar até reconectar. Para operação contínua, prefira a API oficial.</span>
            </div>
        </CxPainel>
    );
};

export default ConectarQrPanel;
